// CMP-AppShell — cadre de toute page authentifiée : TopBar + Sidebar + contenu ; NetworkStatusBar au-dessus ;
// Sidebar masquée sous 900 px (menu en tiroir) ; BottomNav mobile pour Animateur et Membre ;
// session expirée = superposition ACC-P07 avec reconnexion sur place. Maquette : grille 256 px + contenu, marge 22 px 32 px.
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Dialog } from 'radix-ui';
import { lazy, Suspense, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionExpired } from '@/app/session';
import { useMe } from '@/app/me';
import { LazyToaster } from '@/design-system/LazyToaster';
import { NetworkStatusBar } from '@/design-system/NetworkStatusBar';
import { cn } from '@/lib/cn';
import { BOTTOM_NAV_ROLES, navigationFor } from '@/navigation/nav-config';
import { activeTarget, Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

// Chargée à la demande (hors budget initial) ; précachée par le service worker pour rester disponible hors ligne.
const SessionExpiredOverlay = lazy(() => import('./SessionExpiredOverlay').then((m) => ({ default: m.SessionExpiredOverlay })));

const COLLAPSE_KEY = 'wangre.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('nav');
  const { t: tc } = useTranslation();
  const me = useMe();
  const expired = useSessionExpired();
  const groups = navigationFor(me);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const bottomItems = BOTTOM_NAV_ROLES.includes(me.primary_role?.code ?? '') ? groups.flatMap((g) => g.items).slice(0, 5) : [];
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const activeBottom = activeTarget(groups, pathname);

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, value ? '0' : '1');
      } catch {
        /* sans stockage : l'état reste valable pour la session */
      }
      return !value;
    });
  };

  return (
    // --wg-main-pb-mobile : retrait bas de la zone principale au mobile (barre du bas : pb-20 ; sinon py-[22px]), compensé
    // par les pieds collants (un élément collant reste à l'intérieur du retrait de son conteneur défilant).
    <div
      className="flex h-dvh flex-col bg-bg text-text"
      style={{ '--wg-bottom-nav': bottomItems.length > 1 ? '4rem' : '0px', '--wg-main-pb-mobile': bottomItems.length > 1 ? '5rem' : '22px' } as CSSProperties}
    >
      <a href="#main" className="sr-only-focusable absolute top-2 left-2 z-50 rounded-md bg-surface px-3 py-2 text-small shadow-e3">
        {tc('actions.skip_to_content')}
      </a>
      <NetworkStatusBar />
      <div className="flex min-h-0 flex-1">
        <Sidebar groups={groups} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} className="max-[899px]:hidden" />

        <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-n-900/50 min-[900px]:hidden" />
            <Dialog.Content className="fixed inset-y-0 left-0 z-50 shadow-e4 min-[900px]:hidden">
              <Dialog.Title className="sr-only">{t('label')}</Dialog.Title>
              <Dialog.Description className="sr-only">{t('label')}</Dialog.Description>
              <Sidebar groups={groups} onNavigate={() => setMobileOpen(false)} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <TopBar me={me} onOpenNavigation={() => setMobileOpen(true)} />
          <main id="main" tabIndex={0} className={cn('relative flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-8 py-[22px] max-md:px-4', bottomItems.length > 1 && 'max-md:pb-20')}>
            {children ?? <Outlet />}
          </main>
        </div>
      </div>

      {bottomItems.length > 1 && (
        <nav aria-label={t('label')} className="fixed inset-x-0 bottom-0 z-30 flex h-16 border-t border-border bg-surface md:hidden">
          {bottomItems.map((entry) => (
            <Link
              key={entry.id}
              to={entry.to}
              search={entry.search as never}
              aria-current={entry.to === activeBottom ? 'page' : undefined}
              className={cn('flex flex-1 flex-col items-center justify-center gap-0.5 text-[12px] text-text-2 no-underline', entry.to === activeBottom && '!text-primary font-semibold')}
            >
              <entry.icon aria-hidden="true" className="size-5" />
              {t(`items.${entry.label}` as never) as string}
            </Link>
          ))}
        </nav>
      )}

      {expired && (
        <Suspense fallback={null}>
          <SessionExpiredOverlay me={me} />
        </Suspense>
      )}
      <LazyToaster />
    </div>
  );
}
